import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banknote, Smartphone, CreditCard, ShieldCheck, BookUser } from 'lucide-react';
import { toast } from 'react-toastify';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import ModalShell, { MODAL_CANCEL_CLASS, MODAL_SUBMIT_CLASS } from '../common/ModalShell';
import baseURL from '../../utils/baseURL';
import useHospitalCurrency from '../../hooks/useHospitalCurrency';
import { currencySymbol, formatPrice } from '../../utils/currency';

// Collect a tender against ANY invoice — the shared payment path of unified
// billing (POST /api/billing/invoices/:id/payment). The server locks the row,
// refuses overpayment and rebuilds the totals; this modal only asks the two
// questions a cashier answers: how, and how much.

const METHODS = [
  { id: 'cash', labelKey: 'billing.cash', fallback: 'Cash', Icon: Banknote },
  { id: 'upi', labelKey: 'billing.upi', fallback: 'UPI', Icon: Smartphone },
  { id: 'card', labelKey: 'billing.card', fallback: 'Card', Icon: CreditCard },
  { id: 'insurance', labelKey: 'billing.insurance', fallback: 'Insurance', Icon: ShieldCheck },
  { id: 'credit', labelKey: 'billing.credit', fallback: 'Credit', Icon: BookUser },
];

// The amounts are in the hospital's currency (P24): the page that opens this
// modal passes it as `currency`; opened without one, the modal reads it itself.
// It used to print the rupee sign and a rupee icon for every hospital. Unknown
// -> the bare amount and a neutral icon.
const CollectPaymentModal = ({ invoice, currency: currencyProp, onClose, onPaid }) => {
  const { t } = useTranslation();
  const ownCurrency = useHospitalCurrency();
  const currency = currencyProp || ownCurrency;
  const sign = currencySymbol(currency);
  const due = formatPrice(Number(invoice.balance || 0), currency, { decimals: 2 });
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState(String(invoice.balance ?? ''));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);

  const collect = async () => {
    setBusy(true);
    try {
      const axiosInstance = createAuthedAxios();
      await axiosInstance.post(`${baseURL}/billing/invoices/${invoice.invoiceId}/payment`, {
        method, amount: Number(amount), reference: reference.trim() || undefined,
      });
      toast.success(t('billing.collected', 'Payment recorded'));
      onPaid?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('billing.collect_failed', 'Could not record the payment'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={t('billing.collect_title', 'Collect payment')}
      onClose={onClose}
      size="sm"
      testId="collect-payment-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} className={MODAL_CANCEL_CLASS}>
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={collect}
            disabled={busy || !Number(amount)}
            className={MODAL_SUBMIT_CLASS}
            data-testid="pay-confirm"
          >
            {busy && <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin" />}
            {t('billing.record', 'Record payment')}
          </button>
        </>
      )}
    >
        <div className="space-y-4">
          <p className="text-[11px] text-gray-400 -mt-1">{invoice.invoiceNo} · <span data-testid="collect-payment-due">{t('billing.due', { amount: due, defaultValue: `Due ${due}` })}</span></p>
          <div className="grid grid-cols-5 gap-1.5">
            {METHODS.map((m) => (
              <button
                key={m.id}
                onClick={() => setMethod(m.id)}
                className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[10px] font-medium transition-colors cursor-pointer ${method === m.id ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#2D8A8A]' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}
                data-testid={`pay-method-${m.id}`}
              >
                <m.Icon size={16} />
                {t(m.labelKey, m.fallback)}
              </button>
            ))}
          </div>
          <div className="relative">
            {sign ? (
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm text-gray-400" data-testid="pay-amount-sign">{sign}</span>
            ) : (
              <Banknote size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            )}
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
              className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-sm font-semibold"
              data-testid="pay-amount"
            />
          </div>
          {method !== 'cash' && (
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('billing.reference', 'Reference (UTR / auth code / policy no.)')}
              className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#4ABDBD] focus:border-transparent transition-all text-sm"
            />
          )}
        </div>
    </ModalShell>
  );
};

export default CollectPaymentModal;
